/**
 * Hook to fetch BaseTemplate site settings (header URL, title, description, gallery).
 *
 * Uses a module-level singleton so N components calling useSiteSettings() share
 * exactly ONE fetch + ONE 10-second poll — no concurrent duplicate requests.
 */

import { BRAND_PERSON_NAME } from "@/lib/brandConfig";
import { useState, useEffect } from "react";

export interface SiteSettings {
  headerUrl: string;
  title: string;
  description: string;
  logoUrl?: string;
  sobreMi?: string;
  showSobreMi?: boolean;
  galleryImageUrls?: string[];
  bannerImageUrl?: string;
  footerTagline?: string;
  alternativeGalleryEnabled?: boolean;
  alternativeGalleryUrls?: string[];
}

const DEFAULT_SOBRE_MI =
  `${BRAND_PERSON_NAME} es modelo y creadora de contenido argentina con más de cinco años de trayectoria en producción propia, proyectos independientes y construcción de audiencia en redes. Una identidad estética consolidada y una comunidad comprometida la posicionan como una figura de referencia para marcas, campañas comerciales y producciones audiovisuales.`;

/** Estado inicial: vacío para evitar flash de texto hardcodeado antes de que cargue la API */
const INITIAL_SETTINGS: SiteSettings = {
  headerUrl: "/",
  title: "",
  description: "",
  sobreMi: DEFAULT_SOBRE_MI,
};

export const SITE_SETTINGS_UPDATED_LS_KEY = "site_settings_updated";
export const SITE_SETTINGS_UPDATED_EVENT = "site-settings-updated";

function parseSettingsData(data: Record<string, unknown>): SiteSettings {
  return {
    headerUrl: (data.headerUrl as string) ?? "/",
    title: (data.title as string) ?? "",
    description: (data.description as string) ?? "",
    logoUrl: (data.logoUrl as string) || undefined,
    sobreMi: (data.sobreMi as string) ?? DEFAULT_SOBRE_MI,
    showSobreMi: typeof data.showSobreMi === "boolean" ? data.showSobreMi : true,
    galleryImageUrls: Array.isArray(data.galleryImageUrls) ? (data.galleryImageUrls as string[]) : undefined,
    bannerImageUrl: typeof data.bannerImageUrl === "string" && data.bannerImageUrl.trim() ? data.bannerImageUrl : undefined,
    footerTagline: typeof data.footerTagline === "string" && data.footerTagline.trim() ? data.footerTagline.trim() : undefined,
    alternativeGalleryEnabled: typeof data.alternativeGalleryEnabled === "boolean" ? data.alternativeGalleryEnabled : false,
    alternativeGalleryUrls: Array.isArray(data.alternativeGalleryUrls) ? (data.alternativeGalleryUrls as string[]) : undefined,
  };
}

// ── Module-level singleton ────────────────────────────────────────────────────
// One fetch + one poll shared by ALL useSiteSettings() callers on the page.
// Prevents N concurrent requests when N components mount simultaneously.

interface VariantStore {
  settings: SiteSettings;
  isLoading: boolean;
  refCount: number;
  poll: ReturnType<typeof setInterval> | null;
  listeners: Set<() => void>;
}

const stores: Partial<Record<"live" | "test", VariantStore>> = {};

function getStore(variant: "live" | "test"): VariantStore {
  if (!stores[variant]) {
    stores[variant] = {
      settings: INITIAL_SETTINGS,
      isLoading: true,
      refCount: 0,
      poll: null,
      listeners: new Set(),
    };
  }
  return stores[variant]!;
}

function notify(store: VariantStore) {
  store.listeners.forEach((fn) => fn());
}

async function fetchOnce(variant: "live" | "test", store: VariantStore) {
  const url = variant === "test" ? "/api/site-settings?variant=test" : "/api/site-settings";
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), 5000);
  try {
    const res = await fetch(url, { cache: "no-store", signal: controller.signal });
    clearTimeout(tid);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as Record<string, unknown>;
    store.settings = parseSettingsData(data);
    store.isLoading = false;
    notify(store);
  } catch (err) {
    clearTimeout(tid);
    store.isLoading = false;
    if (err instanceof Error && err.name !== "AbortError") {
      console.warn("[useSiteSettings] Fetch error:", err.message);
    }
    notify(store);
  }
}

// ─────────────────────────────────────────────────────────────────────────────

function getInitialSettings(): SiteSettings {
  return INITIAL_SETTINGS;
}

export function useSiteSettings(variant: "live" | "test" = "live") {
  const store = getStore(variant);

  // Seed the initial settings from __TEMPLATE_CONFIG__ on first render if available.
  // Only runs once; subsequent updates come from the shared fetch.
  const [, forceUpdate] = useState(0);
  const [localSettings] = useState<SiteSettings>(() => {
    if (store.refCount === 0) {
      const initial = getInitialSettings();
      if (initial !== INITIAL_SETTINGS) store.settings = initial;
    }
    return store.settings;
  });
  void localSettings; // used only for initial seed

  useEffect(() => {
    const store = getStore(variant);
    const listener = () => forceUpdate((n) => n + 1);
    store.listeners.add(listener);
    store.refCount++;

    // First subscriber: start the singleton poll + event listeners.
    if (store.refCount === 1) {
      void fetchOnce(variant, store);
      store.poll = setInterval(() => void fetchOnce(variant, store), 10_000);

      const onVisibility = () => {
        if (document.visibilityState === "visible") void fetchOnce(variant, store);
      };
      const onPageShow = (e: PageTransitionEvent) => {
        if (e.persisted) void fetchOnce(variant, store);
      };
      const onStorage = (e: StorageEvent) => {
        if (e.key === SITE_SETTINGS_UPDATED_LS_KEY) void fetchOnce(variant, store);
      };
      // Same-tab signal: `storage` only fires in OTHER tabs, so we also listen
      // for a custom window event dispatched from the admin save flow.
      const onLocalUpdate = () => void fetchOnce(variant, store);

      document.addEventListener("visibilitychange", onVisibility);
      window.addEventListener("pageshow", onPageShow as EventListener);
      window.addEventListener("storage", onStorage);
      window.addEventListener(SITE_SETTINGS_UPDATED_EVENT, onLocalUpdate);

      (store as VariantStore & { _cleanup?: () => void })._cleanup = () => {
        document.removeEventListener("visibilitychange", onVisibility);
        window.removeEventListener("pageshow", onPageShow as EventListener);
        window.removeEventListener("storage", onStorage);
        window.removeEventListener(SITE_SETTINGS_UPDATED_EVENT, onLocalUpdate);
      };
    }

    return () => {
      const s = getStore(variant);
      s.listeners.delete(listener);
      s.refCount--;
      if (s.refCount === 0) {
        if (s.poll !== null) { clearInterval(s.poll); s.poll = null; }
        (s as VariantStore & { _cleanup?: () => void })._cleanup?.();
      }
    };
  }, [variant]);

  return { settings: store.settings, isLoading: store.isLoading };
}
